import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC } from 'react';
import styled from 'styled-components';

import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import Image from '@/components/Image';
import { ActionsWrap, Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { MemeResource } from '@/types/resources';
import { api } from '@/utils/api';

import Status from './Status';

const StyledImage = styled(Image)`
  object-fit: contain;
  width: 100%;
  height: auto;
`;

interface Props {
  meme: MemeResource;
}

const TableRow: FC<Props> = ({ meme }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async () => {
    try {
      const confirmed = await alerts.confirm('Are you sure?');

      if (!confirmed) {
        return;
      }

      await api.delete(`/v1/memes/${meme.id}`);

      await queryClient.invalidateQueries(['animated_memes']);

      alerts.close();
    } catch (_) {
      alerts.error();
    }
  };

  const onOpenVideo = () => {
    window.open(meme.video?.versions?.original, '_blank');
  };

  return (
    <tr>
      <Td>
        <StyledImage src={meme.originalImage?.versions?.original} />
      </Td>
      <Td>{meme.title}</Td>
      <Td>{meme.extractedText ?? '-'}</Td>
      <Td>{meme.duration}</Td>
      <Td>
        <Status status={meme.status} />
      </Td>
      <Td>{meme.owner ?? '-'}</Td>
      <Td>
        {DateTime.fromISO(meme.createdAt).toFormat('dd/LL/yyyy')}
        <br />
        <Text as="span" $color="gray" $type="extraSmall">
          {DateTime.fromISO(meme.createdAt).toFormat('HH:mm')}
        </Text>
      </Td>
      <Td>{meme.error ?? '-'}</Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              Delete
            </ActionsDropdownButton>
            {!!meme.video && (
              <ActionsDropdownButton as="button" onClick={onOpenVideo}>
                Open Video
              </ActionsDropdownButton>
            )}
          </ActionsDropdown>
        </ActionsWrap>
      </Td>
    </tr>
  );
};

export default TableRow;
